import React, { useEffect, useState } from "react";
import { Table, Form, Button, Row, Col, Modal, Badge } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";

// Phase 3D, §29. Self-contained — manages its own list + CRUD against
// /api/forms/:formId/versions/:versionId/task-creation-rules, since rules
// are persisted immediately (their own REST resource), unlike sections/
// fields/options which batch-save through FormBuilderPage.jsx's own state.
const TRIGGER_SCOPES = ["REPEATING_ROW", "SUBMISSION"];
const TRIGGER_OPERATORS = ["IN", "EQUALS"];
const CREATION_MODES = ["MANUAL", "ON_SUBMIT", "ON_FINAL_APPROVAL"];
const RESULT_TRIGGER_VALUES = ["FAIL", "BLOCKED", "PASS", "NOT_APPLICABLE", "NOT_EXECUTED"];
const PLACEHOLDERS = [
  "form_name", "assignment_title", "submission_id", "test_case_id", "test_case_title",
  "expected_result", "actual_result", "result_status", "severity", "project_name", "module_name",
];

const initialRule = {
  rule_code: "", name: "", trigger_scope: "REPEATING_ROW", trigger_field_id: "", trigger_operator: "IN",
  trigger_values_json: ["FAIL", "BLOCKED"], creation_mode: "MANUAL", task_type_id: "", default_priority: "",
  title_template: "{{form_name}} — {{test_case_id}} Failed", description_template: "",
  use_assignment_project: true, use_assignment_project_module: true, fixed_project_id: "", fixed_project_module_id: "",
  default_assignee_id: "", copy_submission_evidence: false, is_active: true,
};

export default function FormTaskCreationRuleEditor({ formId, versionId, resultStatusFieldId, readOnly }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectModules, setProjectModules] = useState([]);
  const [users, setUsers] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // rule being edited, or null for "add"
  const [form, setForm] = useState(initialRule);
  const [saving, setSaving] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  const fetchRules = async () => {
    if (!versionId) return;
    setLoading(true);
    try {
      const res = await api.get(`/forms/${formId}/versions/${versionId}/task-creation-rules`);
      setRules(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load task-creation rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, versionId]);

  useEffect(() => {
    Promise.all([
      api.get("/task-types", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/users").catch(() => null),
    ]).then(([ttRes, pRes, uRes]) => {
      setTaskTypes(Array.isArray(ttRes?.data?.data) ? ttRes.data.data : []);
      setProjects(Array.isArray(pRes?.data?.data) ? pRes.data.data : []);
      setUsers(Array.isArray(uRes?.data) ? uRes.data : []);
    });
  }, []);

  const fetchModulesForProject = async (projectId) => {
    if (!projectId) { setProjectModules([]); return; }
    try {
      const res = await api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } });
      setProjectModules(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (_e) {
      setProjectModules([]);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...initialRule, trigger_field_id: resultStatusFieldId || "" });
    setShowModal(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      rule_code: rule.rule_code || "", name: rule.name || "", trigger_scope: rule.trigger_scope || "REPEATING_ROW",
      trigger_field_id: rule.trigger_field_id || "", trigger_operator: rule.trigger_operator || "IN",
      trigger_values_json: rule.trigger_values_json || [], creation_mode: rule.creation_mode || "MANUAL",
      task_type_id: rule.task_type_id || "", default_priority: rule.default_priority || "",
      title_template: rule.title_template || "", description_template: rule.description_template || "",
      use_assignment_project: !!rule.use_assignment_project, use_assignment_project_module: !!rule.use_assignment_project_module,
      fixed_project_id: rule.fixed_project_id || "", fixed_project_module_id: rule.fixed_project_module_id || "",
      default_assignee_id: rule.default_assignee_id || "", copy_submission_evidence: !!rule.copy_submission_evidence,
      is_active: rule.is_active !== 0 && rule.is_active !== false,
    });
    if (rule.fixed_project_id) fetchModulesForProject(rule.fixed_project_id);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const toggleTriggerValue = (value) => {
    setForm((prev) => {
      const exists = prev.trigger_values_json.includes(value);
      return { ...prev, trigger_values_json: exists ? prev.trigger_values_json.filter((v) => v !== value) : [...prev.trigger_values_json, value] };
    });
  };

  const saveRule = async () => {
    if (!form.rule_code.trim() || !form.name.trim() || !form.task_type_id || !form.title_template.trim()) {
      toast.warning("rule code, name, task type and title template are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        trigger_field_id: Number(form.trigger_field_id) || resultStatusFieldId,
        task_type_id: Number(form.task_type_id),
        fixed_project_id: form.fixed_project_id || null,
        fixed_project_module_id: form.fixed_project_module_id || null,
        default_assignee_id: form.default_assignee_id || null,
        default_priority: form.default_priority || null,
      };
      if (editing) {
        await api.put(`/forms/${formId}/versions/${versionId}/task-creation-rules/${editing.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post(`/forms/${formId}/versions/${versionId}/task-creation-rules`, payload);
        toast.success("Rule created");
      }
      closeModal();
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (rule) => {
    try {
      await api.patch(`/forms/${formId}/versions/${versionId}/task-creation-rules/${rule.id}/status`, { is_active: !rule.is_active });
      toast.success(rule.is_active ? "Rule deactivated" : "Rule activated");
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update rule status");
    }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This is blocked if it already has task links or attempts.`)) return;
    try {
      await api.delete(`/forms/${formId}/versions/${versionId}/task-creation-rules/${rule.id}`);
      toast.success("Rule deleted");
      fetchRules();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_TASK_CREATION_RULE_INVALID" && data?.details?.dependents) {
        toast.error("Cannot delete: this rule already has task links or attempts. Deactivate it instead.");
      } else {
        toast.error(data?.message || "Failed to delete rule");
      }
    }
  };

  return (
    <div className="form-task-creation-rule-editor">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong className="small">Task-Creation Rules</strong>
        {!readOnly && <Button size="sm" variant="outline-primary" onClick={openAdd}>+ Add Rule</Button>}
      </div>

      {loading ? <div className="text-muted small">Loading...</div> : (
        <Table size="sm" responsive>
          <thead>
            <tr>
              <th>Code</th><th>Name</th><th>Trigger</th><th>Mode</th><th>Task Type</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td>{rule.rule_code}</td>
                <td>{rule.name}</td>
                <td className="small">{rule.trigger_operator} {JSON.stringify(rule.trigger_values_json)}</td>
                <td><Badge bg="light" text="dark">{rule.creation_mode}</Badge></td>
                <td className="small">{rule.task_type_name}</td>
                <td><Badge bg={rule.is_active ? "success" : "secondary"}>{rule.is_active ? "Active" : "Inactive"}</Badge></td>
                <td>
                  {!readOnly && (
                    <div className="d-flex gap-1">
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(rule)}>Edit</Button>
                      <Button size="sm" variant="outline-warning" onClick={() => toggleStatus(rule)}>{rule.is_active ? "Deactivate" : "Activate"}</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => removeRule(rule)}>Delete</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan="7" className="text-muted small">No task-creation rules configured yet.</td></tr>}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton><Modal.Title>{editing ? "Edit Rule" : "New Task-Creation Rule"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col md={4}>
              <Form.Label className="small">Rule Code</Form.Label>
              <Form.Control size="sm" value={form.rule_code} onChange={(e) => setForm({ ...form, rule_code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} />
            </Col>
            <Col md={8}>
              <Form.Label className="small">Name</Form.Label>
              <Form.Control size="sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Col>

            <Col md={4}>
              <Form.Label className="small">Trigger Scope</Form.Label>
              <Form.Select size="sm" value={form.trigger_scope} onChange={(e) => setForm({ ...form, trigger_scope: e.target.value })}>
                {TRIGGER_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Trigger Operator</Form.Label>
              <Form.Select size="sm" value={form.trigger_operator} onChange={(e) => setForm({ ...form, trigger_operator: e.target.value })}>
                {TRIGGER_OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Creation Mode</Form.Label>
              <Form.Select size="sm" value={form.creation_mode} onChange={(e) => setForm({ ...form, creation_mode: e.target.value })}>
                {CREATION_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </Form.Select>
            </Col>

            <Col md={12}>
              <Form.Label className="small">Trigger Result Value(s) (matched against the option CODE, e.g. FAIL/BLOCKED)</Form.Label>
              <div className="d-flex gap-3 flex-wrap">
                {RESULT_TRIGGER_VALUES.map((v) => (
                  <Form.Check key={v} type="checkbox" label={v} checked={form.trigger_values_json.includes(v)} onChange={() => toggleTriggerValue(v)} />
                ))}
              </div>
            </Col>

            <Col md={6}>
              <Form.Label className="small">Task Type</Form.Label>
              <Form.Select size="sm" value={form.task_type_id} onChange={(e) => setForm({ ...form, task_type_id: e.target.value })}>
                <option value="">Select...</option>
                {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Default Priority</Form.Label>
              <Form.Select size="sm" value={form.default_priority} onChange={(e) => setForm({ ...form, default_priority: e.target.value })}>
                <option value="">(task type default)</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </Form.Select>
            </Col>

            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label className="small mb-0">Title Template</Form.Label>
                <Button size="sm" variant="link" className="py-0" onClick={() => setShowPlaceholders(!showPlaceholders)}>Placeholder reference</Button>
              </div>
              <Form.Control size="sm" value={form.title_template} onChange={(e) => setForm({ ...form, title_template: e.target.value })} />
            </Col>
            {showPlaceholders && (
              <Col md={12}>
                <div className="text-muted small border rounded p-2">
                  {PLACEHOLDERS.map((p) => <code key={p} className="me-2">{`{{${p}}}`}</code>)}
                </div>
              </Col>
            )}
            <Col md={12}>
              <Form.Label className="small">Description Template (optional)</Form.Label>
              <Form.Control as="textarea" rows={2} size="sm" value={form.description_template} onChange={(e) => setForm({ ...form, description_template: e.target.value })} />
            </Col>

            <Col md={4}>
              <Form.Check type="checkbox" label="Use assignment's project" checked={form.use_assignment_project} onChange={(e) => setForm({ ...form, use_assignment_project: e.target.checked })} />
            </Col>
            <Col md={4}>
              <Form.Check type="checkbox" label="Use assignment's module" checked={form.use_assignment_project_module} onChange={(e) => setForm({ ...form, use_assignment_project_module: e.target.checked })} />
            </Col>
            <Col md={4}>
              <Form.Check type="checkbox" label="Copy submission evidence reference" checked={form.copy_submission_evidence} onChange={(e) => setForm({ ...form, copy_submission_evidence: e.target.checked })} />
            </Col>

            {!form.use_assignment_project && (
              <>
                <Col md={6}>
                  <Form.Label className="small">Fixed Project</Form.Label>
                  <Form.Select size="sm" value={form.fixed_project_id} onChange={(e) => { setForm({ ...form, fixed_project_id: e.target.value, fixed_project_module_id: "" }); fetchModulesForProject(e.target.value); }}>
                    <option value="">None</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Form.Select>
                </Col>
                {!form.use_assignment_project_module && (
                  <Col md={6}>
                    <Form.Label className="small">Fixed Module</Form.Label>
                    <Form.Select size="sm" value={form.fixed_project_module_id} onChange={(e) => setForm({ ...form, fixed_project_module_id: e.target.value })} disabled={!form.fixed_project_id}>
                      <option value="">None</option>
                      {projectModules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </Form.Select>
                  </Col>
                )}
              </>
            )}

            <Col md={6}>
              <Form.Label className="small">Default Assignee (optional)</Form.Label>
              <Form.Select size="sm" value={form.default_assignee_id} onChange={(e) => setForm({ ...form, default_assignee_id: e.target.value })}>
                <option value="">None</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={saveRule}>{saving ? "Saving..." : "Save Rule"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
